import React from 'react';

import CloseIcon from '@mui/icons-material/Close';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Typography,
} from '@mui/material';
import { useTheme } from '@mui/material/styles';

import * as amplitude from '@amplitude/analytics-browser';

import { useJWT } from '../contexts/JWTProvider';
import {
  createDarkenedColor,
  createHoverBackground,
} from '../utils/ThemeUtils';

interface SkipAdditionalTSPProps {
  open: boolean;
  onClose: () => void;
  onSkip: () => void;
  existingTSPsCount: number;
}

const SkipAdditionalTSP: React.FC<SkipAdditionalTSPProps> = ({
  open,
  onClose,
  onSkip,
  existingTSPsCount,
}) => {
  const theme = useTheme();
  const jwt = useJWT();
  const primaryColor = (theme.palette?.primary?.main as string) || '#B60207';

  // Use utility functions for hover colors
  const hoverBackground = createHoverBackground(primaryColor, 0.04);
  const darkenedPrimaryColor = createDarkenedColor(primaryColor);

  // Check if there are any redirect URIs available
  const hasSuccessRedirect =
    jwt.redirectURISuccess && jwt.redirectURISuccess !== 'null';
  const hasErrorRedirect =
    jwt.redirectURIError && jwt.redirectURIError !== 'null';
  const hasAnyRedirect = hasSuccessRedirect || hasErrorRedirect;

  // Determine button text based on redirect availability
  const buttonText = hasAnyRedirect
    ? 'KEEP EXISTING INTEGRATIONS'
    : 'KEEP EXISTING INTEGRATIONS\n(CLOSE THIS TAB)';

  const handleSkip = () => {
    amplitude.track('User Skipped Additional TSP Integration', {
      existingTSPsCount,
      timestamp: new Date().toISOString(),
    });
    onSkip();
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth='xs'
      fullWidth
      BackdropProps={{
        style: {
          backgroundColor: 'rgba(0, 0, 0, 0.4)',
        },
      }}
      PaperProps={{
        sx: {
          width: { xs: 'calc(100% - 32px)', sm: '420px' },
          maxWidth: { xs: 'calc(100vw - 32px)', sm: '420px' },
          margin: { xs: 2, sm: 'auto' },
          borderRadius: '4px',
          boxShadow:
            '0px 4px 8px rgba(0, 0, 0, 0.12), 0px 6px 12px rgba(0, 0, 0, 0.12), 0px 1px 16px rgba(0, 0, 0, 0.12)',
        },
      }}
    >
      <DialogTitle
        sx={{
          borderBottom: '1px solid #DCDEDF',
          padding: { xs: '16px 16px', sm: '18px 24px' },
          fontSize: { xs: '18px', sm: '21.2px' },
          fontWeight: 600,
          letterSpacing: '-0.15px',
          position: 'relative',
          pr: { xs: '48px', sm: '56px' }, // Extra space for close button
        }}
      >
        Skip Additional Integrations
        <IconButton
          aria-label='close'
          onClick={onClose}
          sx={{
            position: 'absolute',
            right: { xs: 8, sm: 8 },
            top: { xs: 8, sm: 8 },
            width: { xs: '40px', sm: '40px' },
            height: { xs: '40px', sm: '40px' },
            color: '#5C656A',
            '&:hover': {
              backgroundColor: hoverBackground,
            },
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent
        sx={{
          padding: { xs: '16px 16px 24px', sm: '16px 24px 24px' },
          minHeight: { xs: 'auto', sm: '160px' },
        }}
      >
        <Typography
          sx={{
            fontSize: { xs: '14px', sm: '16px' },
            fontWeight: 700,
            lineHeight: { xs: '20px', sm: '24px' },
            letterSpacing: '0.15px',
            color: '#263238',
            marginTop: { xs: '12px', sm: '16px' },
            marginBottom: { xs: '16px', sm: '24px' },
          }}
        >
          You already have {existingTSPsCount}{' '}
          {existingTSPsCount > 1 ? 'integrations' : 'integration'}
        </Typography>

        <Typography
          sx={{
            fontSize: { xs: '14px', sm: '16px' },
            lineHeight: { xs: '20px', sm: '24px' },
            letterSpacing: '0.15px',
            color: '#263238',
          }}
        >
          Are you sure you want to exit without adding or updating your
          telematics integrations?
        </Typography>
      </DialogContent>

      <DialogActions
        sx={{
          padding: { xs: '16px 16px 24px', sm: '16px 24px 24px' },
          gap: { xs: '8px', sm: '8px' },
          justifyContent: 'flex-end',
          flexDirection: { xs: 'column', sm: 'row' },
        }}
      >
        <Button
          onClick={handleSkip}
          sx={{
            fontSize: { xs: '16px', sm: '14px' },
            fontWeight: 700,
            letterSpacing: '0.75px',
            textTransform: 'uppercase',
            padding: { xs: '12px 16px', sm: '12px' },
            height: { xs: '48px', sm: 'auto' },
            minHeight: hasAnyRedirect ? 'auto' : { xs: '60px', sm: 'auto' }, // Extra height for multi-line text
            width: { xs: '100%', sm: 'auto' },
            order: { xs: 2, sm: 1 },
            color: primaryColor,
            whiteSpace: 'pre-line', // Allow line breaks to work
            lineHeight: hasAnyRedirect ? 'normal' : { xs: 1.2, sm: 1.3 }, // Tighter line spacing for multi-line
            '&:hover': {
              backgroundColor: hoverBackground,
            },
          }}
        >
          {buttonText}
        </Button>
        <Button
          variant='contained'
          onClick={onClose}
          sx={{
            backgroundColor: primaryColor,
            color: '#FFFFFF',
            fontSize: { xs: '16px', sm: '14px' },
            fontWeight: 700,
            letterSpacing: '0.75px',
            textTransform: 'uppercase',
            padding: { xs: '12px 16px', sm: '12px' },
            height: { xs: '48px', sm: 'auto' },
            width: { xs: '100%', sm: 'auto' },
            order: { xs: 1, sm: 2 },
            boxShadow:
              '0px 1px 2px rgba(0, 0, 0, 0.16), 0px 2px 4px rgba(0, 0, 0, 0.12), 0px 1px 8px rgba(0, 0, 0, 0.1)',
            '&:hover': {
              backgroundColor: darkenedPrimaryColor,
            },
          }}
        >
          CONTINUE WITH INTEGRATION
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default SkipAdditionalTSP;
